import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

const ManualReviewResolutionIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const ManualReviewResolutionSha256Schema = Type.String({ pattern: "^[a-f0-9]{64}$" });
const ManualReviewResolutionDateTimeSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}\\.[0-9]{3}Z$",
});
const ManualReviewResolutionNoteSchema = Type.String({ minLength: 2, maxLength: 500 });

export const ManualReviewResolutionCategorySchema = Type.Union([
  Type.Literal("PAYMENT_RECONCILIATION"),
  Type.Literal("ORDER_SYNC_REVIEW"),
  Type.Literal("COACH_SERVICE_REVIEW"),
]);
export type ManualReviewResolutionCategory = Static<typeof ManualReviewResolutionCategorySchema>;

export const ManualReviewResolutionCodeSchema = Type.Union([
  Type.Literal("MANUALLY_RECONCILED"),
  Type.Literal("NOT_RECEIVED"),
  Type.Literal("AMOUNT_MISMATCH"),
  Type.Literal("DUPLICATE_PAYMENT"),
  Type.Literal("KEEP_ISOLATED"),
  Type.Literal("TRANSFER_TO_FINANCE"),
  Type.Literal("TRANSFER_TO_OPERATIONS"),
]);
export type ManualReviewResolutionCode = Static<typeof ManualReviewResolutionCodeSchema>;

export function manualReviewResolutionCodeMatchesCategory(
  category: ManualReviewResolutionCategory,
  resolutionCode: ManualReviewResolutionCode,
): boolean {
  if (category === "PAYMENT_RECONCILIATION") {
    return ["MANUALLY_RECONCILED", "NOT_RECEIVED", "AMOUNT_MISMATCH", "DUPLICATE_PAYMENT"].includes(
      resolutionCode,
    );
  }
  if (category === "ORDER_SYNC_REVIEW") {
    return resolutionCode === "KEEP_ISOLATED" || resolutionCode === "TRANSFER_TO_FINANCE";
  }
  return resolutionCode === "KEEP_ISOLATED" || resolutionCode === "TRANSFER_TO_OPERATIONS";
}

const ManualReviewResolutionRequestFields = {
  category: ManualReviewResolutionCategorySchema,
  resolutionCode: ManualReviewResolutionCodeSchema,
  note: ManualReviewResolutionNoteSchema,
};

export const PreviewManualReviewResolutionRequestSchema = Type.Object(
  ManualReviewResolutionRequestFields,
  { additionalProperties: false },
);
export type PreviewManualReviewResolutionRequest = Static<
  typeof PreviewManualReviewResolutionRequestSchema
>;

export function isPreviewManualReviewResolutionRequest(
  value: unknown,
): value is PreviewManualReviewResolutionRequest {
  return (
    Value.Check(PreviewManualReviewResolutionRequestSchema, value) &&
    value.note === value.note.trim() &&
    manualReviewResolutionCodeMatchesCategory(value.category, value.resolutionCode)
  );
}

export const ManualReviewResolutionPreviewReadModelSchema = Type.Object(
  {
    kind: Type.Literal("MANUAL_REVIEW_RESOLUTION"),
    workItemId: ManualReviewResolutionIdSchema,
    ...ManualReviewResolutionRequestFields,
    title: Type.String({ minLength: 2, maxLength: 120 }),
    currentReason: Type.String({ minLength: 1, maxLength: 160 }),
    amountFen: Type.Union([
      Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    originalDocumentLabel: Type.String({ minLength: 2, maxLength: 120 }),
    consequences: Type.Array(Type.String({ minLength: 2, maxLength: 300 }), {
      minItems: 1,
      maxItems: 8,
      uniqueItems: true,
    }),
    nextSteps: Type.Array(Type.String({ minLength: 2, maxLength: 300 }), {
      minItems: 1,
      maxItems: 8,
      uniqueItems: true,
    }),
    sourceReferenceSha256: ManualReviewResolutionSha256Schema,
    businessFactSha256: ManualReviewResolutionSha256Schema,
    commandSha256: ManualReviewResolutionSha256Schema,
    canSubmit: Type.Boolean(),
    blocker: Type.Union([Type.String({ minLength: 2, maxLength: 300 }), Type.Null()]),
    generatedAt: ManualReviewResolutionDateTimeSchema,
  },
  { additionalProperties: false },
);
export type ManualReviewResolutionPreviewReadModel = Static<
  typeof ManualReviewResolutionPreviewReadModelSchema
>;

export function isManualReviewResolutionPreviewReadModel(
  value: unknown,
): value is ManualReviewResolutionPreviewReadModel {
  return (
    Value.Check(ManualReviewResolutionPreviewReadModelSchema, value) &&
    value.note === value.note.trim() &&
    manualReviewResolutionCodeMatchesCategory(value.category, value.resolutionCode) &&
    value.canSubmit === (value.blocker === null)
  );
}

export const CreateManualReviewResolutionApprovalCommandSchema = Type.Object(
  {
    workItemId: ManualReviewResolutionIdSchema,
    ...ManualReviewResolutionRequestFields,
    sourceReferenceSha256: ManualReviewResolutionSha256Schema,
    businessFactSha256: ManualReviewResolutionSha256Schema,
    commandSha256: ManualReviewResolutionSha256Schema,
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type CreateManualReviewResolutionApprovalCommand = Static<
  typeof CreateManualReviewResolutionApprovalCommandSchema
>;

export function isCreateManualReviewResolutionApprovalCommand(
  value: unknown,
): value is CreateManualReviewResolutionApprovalCommand {
  return (
    Value.Check(CreateManualReviewResolutionApprovalCommandSchema, value) &&
    value.note === value.note.trim() &&
    manualReviewResolutionCodeMatchesCategory(value.category, value.resolutionCode)
  );
}

export const ManualReviewResolutionApprovalSnapshotSchema = Type.Object(
  {
    kind: Type.Literal("MANUAL_REVIEW_RESOLUTION"),
    snapshotVersion: Type.Literal(1),
    workItemId: ManualReviewResolutionIdSchema,
    ...ManualReviewResolutionRequestFields,
    title: Type.String({ minLength: 2, maxLength: 120 }),
    currentReason: Type.String({ minLength: 1, maxLength: 160 }),
    amountFen: Type.Union([
      Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    originalDocumentLabel: Type.String({ minLength: 2, maxLength: 120 }),
    consequences: Type.Array(Type.String({ minLength: 2, maxLength: 300 }), {
      minItems: 1,
      maxItems: 8,
      uniqueItems: true,
    }),
    nextSteps: Type.Array(Type.String({ minLength: 2, maxLength: 300 }), {
      minItems: 1,
      maxItems: 8,
      uniqueItems: true,
    }),
    sourceReferenceSha256: ManualReviewResolutionSha256Schema,
    commandSha256: ManualReviewResolutionSha256Schema,
    businessFactSha256: ManualReviewResolutionSha256Schema,
    capturedAt: ManualReviewResolutionDateTimeSchema,
  },
  { additionalProperties: false },
);
export type ManualReviewResolutionApprovalSnapshot = Static<
  typeof ManualReviewResolutionApprovalSnapshotSchema
>;

export function isManualReviewResolutionApprovalSnapshot(
  value: unknown,
): value is ManualReviewResolutionApprovalSnapshot {
  return (
    Value.Check(ManualReviewResolutionApprovalSnapshotSchema, value) &&
    value.note === value.note.trim() &&
    manualReviewResolutionCodeMatchesCategory(value.category, value.resolutionCode)
  );
}
